import React, { useState } from "react";
import SimpleReactValidator from "simple-react-validator";
import { toast } from "react-toastify";
import { useLanguage } from "../../context/LanguageContext";

// =========================================================================
// 🔑 KONFIGURASI PENGIRIMAN EMAIL
// Anda dapat memasukkan Access Key Web3Forms Anda di sini jika ada.
// =========================================================================
const WEB3FORMS_ACCESS_KEY =
  process.env.REACT_APP_WEB3FORMS_KEY || "YOUR_WEB3FORMS_ACCESS_KEY_HERE";

const ContactForm: React.FC = () => {
  const { t } = useLanguage();

  const [forms, setForms] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });

  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [submittedMessage, setSubmittedMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [validator] = useState(
    new SimpleReactValidator({
      className: "errorMessage",
      messages: {
        required: t.contact.validation.required,
        email: t.contact.validation.email,
        numeric: t.contact.validation.numeric,
      },
    })
  );

  const changeHandler = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setForms({ ...forms, [name]: value });

    if (validator.allValid()) validator.hideMessages();
    else validator.showMessages();
  };

  // ==========================================
  // 1. KIRIM VIA EMAIL RESMI
  // ==========================================
  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validator.allValid()) {
      validator.showMessages();
      toast.error("Mohon lengkapi semua kolom inputan yang wajib diisi.");
      return;
    }

    setIsSendingEmail(true);
    setErrorMessage(null);
    setSubmittedMessage(null);

    const formData = new FormData();
    formData.append("access_key", WEB3FORMS_ACCESS_KEY);
    formData.append(
      "subject",
      `[KONSULTASI AI] ${forms.name} - ${forms.service}`
    );
    formData.append("from_name", forms.name);
    formData.append("to_email", t.company.email);
    formData.append("name", forms.name);
    formData.append("email", forms.email);
    formData.append("phone", forms.phone);
    formData.append("service", forms.service);
    formData.append("message", forms.message);

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: formData,
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSubmittedMessage(
          `${t.contact.emailSuccess} (Ditujukan ke: ${t.company.email})`
        );
        toast.success(t.contact.emailSuccess);

        setForms({
          name: "",
          email: "",
          phone: "",
          service: "",
          message: "",
        });
        validator.hideMessages();
        setTimeout(() => setSubmittedMessage(null), 8000);
      } else {
        const errDetail =
          result.message || "Access Key belum disetel atau tidak valid.";
        console.error("Gagal kirim email:", errDetail);
        setErrorMessage(
          `Gagal mengirim email: ${errDetail}. Silakan gunakan tombol "Kirim Cepat via WhatsApp" di bawah agar langsung terhubung dengan tim kami.`
        );
        toast.error(`Error pengiriman email: ${errDetail}`);
      }
    } catch (err: any) {
      console.error("Network error:", err);
      setErrorMessage(
        "Koneksi server email tidak merespon. Silakan gunakan tombol WhatsApp di bawah."
      );
      toast.error("Gagal menghubungi server email. Silakan gunakan WhatsApp.");
    } finally {
      setIsSendingEmail(false);
    }
  };

  // ==========================================
  // 2. KIRIM CEPAT VIA WHATSAPP (LENGKAP SEMUA DATA)
  // ==========================================
  const handleWhatsAppSubmit = () => {
    if (!validator.allValid()) {
      validator.showMessages();
      toast.error(
        "Mohon lengkapi Nama, Email, Nomor Telepon, Layanan, dan Pesan terlebih dahulu."
      );
      return;
    }

    const messageText =
      `Halo Tim ${t.company.name},\n\n` +
      `Saya ingin mengajukan Permintaan Konsultasi Solusi Integrasi AI dengan rincian sebagai berikut:\n\n` +
      `• *Nama Lengkap:* ${forms.name}\n` +
      `• *Email Korporat:* ${forms.email}\n` +
      `• *No. Telepon / WA:* ${forms.phone}\n` +
      `• *Layanan AI:* ${forms.service}\n` +
      `• *Kebutuhan / Kasus Bisnis:*\n${forms.message}\n\n` +
      `Mohon dapat dihubungi untuk evaluasi dan diskusi teknis lebih lanjut. Terima kasih.`;

    const cleanPhone = t.company.whatsapp.replace(/[^0-9]/g, "");
    const waUrl = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(
      messageText
    )}`;

    toast.info("Membuka WhatsApp resmi PT Prima Cipta Artifisial...");
    window.open(waUrl, "_blank");
  };

  return (
    <form onSubmit={handleEmailSubmit} className="pca-form-container">
      {/* Success Status Banner */}
      {submittedMessage && (
        <div
          style={{
            padding: "16px 20px",
            backgroundColor: "rgba(0, 255, 151, 0.15)",
            border: "1px solid #00FF97",
            borderRadius: "14px",
            color: "#00FF97",
            marginBottom: "25px",
            fontSize: "14.5px",
            lineHeight: "1.5",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              fontWeight: 700,
            }}
          >
            <i className="fas fa-check-circle" style={{ fontSize: "20px" }}></i>
            <span>{submittedMessage}</span>
          </div>
          <div
            style={{
              fontSize: "12px",
              color: "rgba(255,255,255,0.75)",
              marginTop: "8px",
            }}
          >
            Tim engineering PT Prima Cipta Artifisial akan meninjau dan merespon dalam 1x24 jam kerja.
          </div>
        </div>
      )}

      {/* Error Status Banner */}
      {errorMessage && (
        <div
          style={{
            padding: "16px 20px",
            backgroundColor: "rgba(255, 95, 86, 0.15)",
            border: "1px solid #FF5F56",
            borderRadius: "14px",
            color: "#FF5F56",
            marginBottom: "25px",
            fontSize: "14px",
            lineHeight: "1.5",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              fontWeight: 700,
            }}
          >
            <i
              className="fas fa-exclamation-circle"
              style={{ fontSize: "18px" }}
            ></i>
            <span>{errorMessage}</span>
          </div>
        </div>
      )}

      <div className="row g-4">
        {/* Row 1, Col 1: Nama Lengkap */}
        <div className="col-lg-6 col-md-6">
          <div className="pca-form-group">
            <label className="pca-form-label" htmlFor="author-name">
              {t.contact.nameLabel}
            </label>
            <div className="pca-input-wrapper">
              <i className="fas fa-user pca-input-icon"></i>
              <input
                id="author-name"
                name="name"
                type="text"
                className="pca-form-control"
                placeholder="cth. Budi Santoso"
                value={forms.name}
                onChange={changeHandler}
                required
              />
            </div>
            {validator.message("name", forms.name, "required")}
          </div>
        </div>

        {/* Row 1, Col 2: No. Telepon / WhatsApp */}
        <div className="col-lg-6 col-md-6">
          <div className="pca-form-group">
            <label className="pca-form-label" htmlFor="author-phone">
              {t.contact.phoneLabel}
            </label>
            <div className="pca-input-wrapper">
              <i className="fas fa-phone-alt pca-input-icon"></i>
              <input
                id="author-phone"
                name="phone"
                type="text"
                className="pca-form-control"
                placeholder="+62 812 3456 7890"
                value={forms.phone}
                onChange={changeHandler}
                required
              />
            </div>
            {validator.message("phone", forms.phone, "required")}
          </div>
        </div>

        {/* Row 2, Col 1: Email Korporat */}
        <div className="col-lg-6 col-md-6">
          <div className="pca-form-group">
            <label className="pca-form-label" htmlFor="author-email">
              {t.contact.emailLabel}
            </label>
            <div className="pca-input-wrapper">
              <i className="fas fa-envelope pca-input-icon"></i>
              <input
                id="author-email"
                name="email"
                type="email"
                className="pca-form-control"
                placeholder="budi@perusahaan.co.id"
                value={forms.email}
                onChange={changeHandler}
                required
              />
            </div>
            {validator.message("email", forms.email, "required|email")}
          </div>
        </div>

        {/* Row 2, Col 2: Pilih Layanan Integrasi AI */}
        <div className="col-lg-6 col-md-6">
          <div className="pca-form-group">
            <label className="pca-form-label">
              {t.contact.serviceLabel}
            </label>
            <div className="pca-input-wrapper">
              <i className="fas fa-layer-group pca-input-icon"></i>
              <select
                name="service"
                value={forms.service}
                onChange={changeHandler}
                required
                className="pca-select-control"
              >
                <option value="">
                  -- Pilih Layanan Integrasi AI --
                </option>
                {t.contact.serviceOptions.map((opt, i) => (
                  <option key={i} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
              <i className="fas fa-chevron-down pca-select-arrow"></i>
            </div>
            {validator.message("service", forms.service, "required")}
          </div>
        </div>

        {/* Row 3: Pesan / Kebutuhan Bisnis */}
        <div className="col-12">
          <div className="pca-form-group">
            <label className="pca-form-label" htmlFor="massage">
              {t.contact.messageLabel}
            </label>
            <div className="pca-input-wrapper">
              <i className="fas fa-comment-alt pca-textarea-icon"></i>
              <textarea
                id="massage"
                name="message"
                className="pca-textarea-control"
                placeholder="Jelaskan kebutuhan integrasi sistem, database, atau skenario bisnis perusahaan Anda..."
                value={forms.message}
                onChange={changeHandler}
                required
              ></textarea>
            </div>
            {validator.message("message", forms.message, "required")}
          </div>
        </div>
      </div>

      {/* Action Buttons (Email & WhatsApp) */}
      <div className="mt-4 pt-2">
        <div
          style={{
            fontSize: "13px",
            color: "rgba(255, 255, 255, 0.7)",
            marginBottom: "14px",
            fontWeight: 600,
            letterSpacing: "0.5px",
          }}
        >
          {t.contact.chooseMethod}
        </div>

        <div className="d-flex flex-wrap align-items-center gap-3">
          {/* 1. BUTTON SEND TO EMAIL */}
          {/* <button
            type="submit"
            disabled={isSendingEmail}
            className="pca-btn-email"
            style={{
              cursor: isSendingEmail ? "not-allowed" : "pointer",
              opacity: isSendingEmail ? 0.75 : 1,
            }}
          >
            {isSendingEmail ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                <span>{t.contact.emailSending}</span>
              </>
            ) : (
              <>
                <i className="fas fa-envelope"></i>
                <span>{t.contact.submitEmailBtn}</span>
              </>
            )}
          </button> */}

          {/* 2. BUTTON SEND TO WHATSAPP */}
          <button
            type="button"
            onClick={handleWhatsAppSubmit}
            className="pca-btn-whatsapp"
            title="Kirim konsultasi lengkap langsung ke WhatsApp Official PT Prima Cipta Artifisial"
          >
            <i className="fab fa-whatsapp" style={{ fontSize: "18px" }}></i>
            <span>{t.contact.submitWhatsAppBtn}</span>
          </button>
        </div>
      </div>
    </form>
  );
};

export default ContactForm;
